import { Alarm } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/en-gb";
import { useEffect, useRef, useState } from "react";
import { ChipContainer, DateLabel } from "./dateChip.styles";

export interface DateChipProps {
  /** Selected date value (controlled mode) */
  value?: Date | null;

  /** Callback when date changes */
  onChange?: (date: Date | null) => void;

  /** Initial date value (uncontrolled mode) */
  defaultValue?: Date | null;

  /** Whether the chip is disabled */
  disabled?: boolean;

  /** Minimum selectable date (defaults to today) */
  minDate?: Date;

  /** Custom label */
  label: string;

  /** Custom icon */
  icon?: React.ReactElement;
}

export const DateChip = ({
  value,
  onChange,
  defaultValue,
  disabled = false,
  minDate,
  label,
  icon = <Alarm sx={{ fontSize: 18 }} />,
}: DateChipProps) => {
  // Determine if component is controlled
  const isControlled = value !== undefined;

  // Ref for the chip element to anchor the date picker
  const chipRef = useRef<HTMLDivElement>(null);

  // Internal state for uncontrolled mode
  const [internalDate, setInternalDate] = useState<Date | null>(defaultValue || null);

  // State for date picker open/closed
  const [pickerOpen, setPickerOpen] = useState(false);

  // Get the current date value (controlled or uncontrolled)
  const currentDate = isControlled ? value : internalDate;

  // Convert to dayjs for the picker
  const dayjsValue = currentDate ? dayjs(currentDate) : null;

  // Set minimum date (default to today)
  const minDateDayjs = minDate ? dayjs(minDate) : dayjs().startOf("day");

  // Update internal state when defaultValue changes (uncontrolled mode only)
  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setInternalDate(defaultValue);
    }
  }, [defaultValue, isControlled]);

  const handleDateChange = (newValue: Dayjs | null) => {
    const newDate = newValue ? newValue.toDate() : null;

    if (!isControlled) {
      setInternalDate(newDate);
    }

    if (onChange) {
      onChange(newDate);
    }
  };

  const handleChipClick = () => {
    if (!disabled) {
      setPickerOpen(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!disabled && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setPickerOpen(true);
    }
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleDateChange(null);
  };

  // Format the chip label
  const getChipLabel = () => {
    if (currentDate) {
      const formattedDate = dayjs(currentDate).format("DD/MM/YYYY");
      return (
        <DateLabel>
          <span className="label-text">{label}</span>
          <span className="date-value">{formattedDate}</span>
        </DateLabel>
      );
    }
    return label;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <ChipContainer
        ref={chipRef}
        icon={currentDate ? undefined : icon}
        label={getChipLabel()}
        variant="outlined"
        onClick={handleChipClick}
        onKeyDown={handleKeyDown}
        onDelete={currentDate ? handleDelete : undefined}
        disabled={disabled}
        hasDate={!!currentDate}
        isOpen={pickerOpen}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={
          currentDate
            ? `${label} selected: ${dayjs(currentDate).format("MMMM D, YYYY")}. Press Enter to change or Delete to clear.`
            : `${label} not set. Press Enter to select a date.`
        }
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
      />
      <DatePicker
        value={dayjsValue}
        onChange={handleDateChange}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onOpen={() => setPickerOpen(true)}
        minDate={minDateDayjs}
        disabled={disabled}
        slotProps={{
          textField: {
            sx: { display: "none" },
          },
          popper: {
            anchorEl: chipRef.current,
            placement: "bottom-start",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 8],
                },
              },
            ],
          },
          day: {
            sx: {
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.main",
                },
                "&:focus": {
                  backgroundColor: "primary.main",
                },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
